/**
 * MySQL Script Provider
 *
 * Implements SQL script generation for MySQL/MariaDB.
 * Uses backtick quoting, LIMIT syntax, AUTO_INCREMENT, and ENGINE=InnoDB.
 */

import type {
  ScriptProvider,
  TableScriptType,
  TableScriptContext,
  DatabaseScriptType,
  DatabaseScriptContext,
  ColumnMetadata,
  NewObjectTemplateContext,
} from '../types/scriptProvider';
import { defaultValuesInsert } from '../../../utils/defaultValuesInsert';
import { decodeMysqlLiteral, quoteMysqlLiteral } from './mysqlLiteral';
import { SELECT_TOP_ROW_LIMIT } from '../selectTopRowLimit';
import { appendIndexStatements } from './indexStatements';
import { resolvePrimaryKey } from './primaryKeyClause';

/** Quote a MySQL identifier with backticks, escaping embedded backticks */
function quoteId(name: string): string {
  return `\`${name.replace(/`/g, '``')}\``;
}

/** Create a fully qualified MySQL table name: `database`.`table` (skips schema if empty) */
function qualifyName(schema: string, table: string): string {
  if (!schema) return quoteId(table);
  return `${quoteId(schema)}.${quoteId(table)}`;
}

/** Prefix for template qualification: `database`. or empty if no schema */
function schemaPrefix(schema: string): string {
  return schema ? `${quoteId(schema)}.` : '';
}

/**
 * Split the trailing numeric attributes MySQL carries in `COLUMN_TYPE`
 * (`bigint unsigned`, `decimal(10,2) unsigned zerofill`) off the type name.
 * They are part of the column's declared type: dropping them narrows the
 * column, so the type mapping below has to re-append them verbatim.
 */
function splitNumericAttributes(dataType: string): { base: string; attributes: string } {
  const match = /^(.*?)((?:\s+(?:unsigned|zerofill))+)\s*$/i.exec(dataType.trim());
  if (!match) return { base: dataType.trim(), attributes: '' };
  const attributes = match[2].trim().split(/\s+/).map(a => a.toUpperCase()).join(' ');
  return { base: match[1].trim(), attributes: ` ${attributes}` };
}

/**
 * Upper-case a type NAME while leaving any parenthesised payload verbatim.
 * `enum('a','b')` must not become `ENUM('A','B')` — MySQL's `MODIFY COLUMN`
 * restates the whole definition, so an upper-cased payload would rewrite the
 * enum's members (and a SET's) on the server.
 */
function upperTypeName(dataType: string): string {
  const open = dataType.indexOf('(');
  if (open === -1) return dataType.toUpperCase();
  return `${dataType.slice(0, open).toUpperCase()}${dataType.slice(open)}`;
}

/** Map MySQL data types to their CREATE TABLE representation */
function formatColumnDataType(col: ColumnMetadata): string {
  const { maxLength, precision, scale, isIdentity } = col;
  const { base, attributes } = splitNumericAttributes(col.dataType);
  const lower = base.toLowerCase();

  // AUTO_INCREMENT columns: the declared integer type, width and attributes
  // preserved. Collapsing everything unrecognized to INT narrowed a
  // `bigint unsigned` identity column in the Table Designer's baseline, and
  // MySQL's `MODIFY COLUMN` restates the whole definition, so the first edit
  // of that column would have shrunk it on the server.
  if (isIdentity) {
    if (['bigint', 'int8'].includes(lower)) return `BIGINT${attributes}`;
    if (['smallint', 'int2'].includes(lower)) return `SMALLINT${attributes}`;
    if (['tinyint'].includes(lower)) return `TINYINT${attributes}`;
    if (['int', 'integer', 'int4'].includes(lower)) return `INT${attributes}`;
    return `${upperTypeName(base)}${attributes}`;
  }

  // Types with length
  if (['varchar', 'character varying', 'var_string'].includes(lower)) {
    return maxLength ? `VARCHAR(${maxLength})` : 'VARCHAR(255)';
  }
  if (['char', 'character'].includes(lower)) {
    return maxLength ? `CHAR(${maxLength})` : 'CHAR(1)';
  }

  // Types with precision and scale
  if (['decimal', 'numeric'].includes(lower)) {
    if (precision != null && scale != null) return `DECIMAL(${precision}, ${scale})${attributes}`;
    if (precision != null) return `DECIMAL(${precision})${attributes}`;
    return `DECIMAL${attributes}`;
  }

  // Common MySQL type normalization
  const typeMap: Record<string, string> = {
    int: 'INT', integer: 'INT', int4: 'INT',
    bigint: 'BIGINT', int8: 'BIGINT',
    smallint: 'SMALLINT', int2: 'SMALLINT',
    tinyint: 'TINYINT', mediumint: 'MEDIUMINT',
    float: 'FLOAT', double: 'DOUBLE',
    boolean: 'TINYINT(1)', bool: 'TINYINT(1)',
    text: 'TEXT', tinytext: 'TINYTEXT', mediumtext: 'MEDIUMTEXT', longtext: 'LONGTEXT',
    blob: 'BLOB', tinyblob: 'TINYBLOB', mediumblob: 'MEDIUMBLOB', longblob: 'LONGBLOB',
    date: 'DATE', time: 'TIME', datetime: 'DATETIME', timestamp: 'TIMESTAMP', year: 'YEAR',
    json: 'JSON', enum: 'ENUM', set: 'SET',
  };

  return `${typeMap[lower] || upperTypeName(base)}${attributes}`;
}

/** Base type name of a MySQL `COLUMN_TYPE` string (`varchar(20)` -> `varchar`). */
function baseTypeName(dataType: string): string {
  return splitNumericAttributes(dataType).base.toLowerCase().split('(')[0].trim().split(/\s+/)[0];
}

/** Types that carry a collation. `binary`/`varbinary`/`blob` deliberately do not. */
const CHARACTER_TYPES = new Set([
  'char', 'character', 'varchar', 'var_string', 'string',
  'tinytext', 'text', 'mediumtext', 'longtext', 'enum', 'set',
]);

/** Types whose `COLUMN_DEFAULT` is a bare number MySQL must receive unquoted. */
const NUMERIC_TYPES = new Set([
  'int', 'integer', 'int4', 'bigint', 'int8', 'smallint', 'int2', 'tinyint', 'mediumint',
  'decimal', 'numeric', 'dec', 'fixed', 'float', 'double', 'real', 'bit', 'year',
  'bool', 'boolean', 'serial',
]);

/** `CURRENT_TIMESTAMP` family — MySQL reports these verbatim, not as literals. */
const TEMPORAL_DEFAULT_KEYWORD =
  /^(current_timestamp|current_date|current_time|localtime|localtimestamp|now|curdate|curtime|utc_timestamp|utc_date|utc_time)(\s*\(\s*\d*\s*\))?$/i;

/**
 * Render a column's DEFAULT clause value, or null when the column has none.
 *
 * `information_schema.COLUMNS.COLUMN_DEFAULT` — the source of `defaultValue`
 * for MySQL — reports a string/temporal/enum/set literal WITHOUT its quotes
 * (`abc`, `2020-01-01`, `x,y`), so emitting it verbatim produced `DEFAULT abc`,
 * which MySQL reads as an identifier. Numbers, `b'…'`/`0x…` literals and the
 * `CURRENT_TIMESTAMP` family are reported in a form MySQL accepts as-is.
 *
 * MySQL 8.0.13+ expression defaults are reported as the bare expression
 * (`uuid()`), which MySQL only accepts back inside parentheses. `COLUMN_DEFAULT`
 * alone cannot separate those from a literal that happens to look like a call —
 * the `EXTRA` flag `DEFAULT_GENERATED` carries that bit, and metadata threads it
 * through as `defaultIsExpression`. When the flag is set it decides; only an
 * UNSET flag (an engine/caller that does not report it) falls back to guessing
 * from the value's shape, where a function-call shape reads as an expression.
 *
 * The flag is unset on MariaDB and on MySQL 5.7 (`mysqlReportsUnquotedDefaults`
 * gates it on >= 8.0.13), and those two spell `COLUMN_DEFAULT` differently: 5.7
 * reports a string default WITHOUT its quotes (`x`), MariaDB >= 10.2.7 reports
 * it ALREADY SQL-ENCODED (`'x'`) and spells an explicit `DEFAULT NULL` as the
 * string `NULL`. Quoting both the same way made MariaDB's `'x'` come back as
 * `DEFAULT '''x'''` and its `NULL` as `DEFAULT 'NULL'` — this is the Table
 * Designer's BASELINE, so the very first edit of the column planned a
 * `MODIFY COLUMN` that changed the default and reported the edit saved.
 *
 * Which of the two spellings a value is in comes from the SERVER, never from
 * the value: `defaultEncoding` (`ColumnMetadata`, set by `metadataService` from
 * the connection's detected variant). A `'sql'` value that already IS one whole
 * single-quoted literal is emitted verbatim and a bare `NULL` means no default;
 * a `'raw'` one goes through the encoder whatever it looks like. Reading the
 * value's shape instead corrupted every quoted-LOOKING MySQL value — a 5.7
 * column declared `DEFAULT '''x'''` reports the three characters `'x'`, which
 * was emitted back as `DEFAULT 'x'`. Only an UNSET encoding (a caller that does
 * not report the variant) still guesses from the shape, the behaviour that
 * predates the field.
 */
function formatDefaultValue(col: ColumnMetadata): string | null {
  const value = col.defaultValue;
  if (value == null) return null;
  if (col.defaultIsExpression === true) {
    // A keyword emits bare; every other expression needs MySQL's parentheses.
    if (TEMPORAL_DEFAULT_KEYWORD.test(value)) return value;
    return /^\([\s\S]*\)$/.test(value) ? value : `(${value})`;
  }
  if (col.defaultIsExpression === false) {
    // A literal, whatever it looks like — `uuid()` stored in a VARCHAR included.
    return NUMERIC_TYPES.has(baseTypeName(col.dataType)) ? value : quoteMysqlLiteral(value);
  }
  // Flag UNSET — MariaDB / 5.7 / a caller that does not report the bit.
  // The SQL-encoded (MariaDB) spellings, applied only where the server said so.
  if (col.defaultEncoding !== 'raw') {
    if (value === 'NULL') return null;
    if (decodeMysqlLiteral(value) !== undefined) return value;
  }
  if (NUMERIC_TYPES.has(baseTypeName(col.dataType))) return value;
  if (TEMPORAL_DEFAULT_KEYWORD.test(value)) return value;
  if (/^0x[0-9a-f]+$/i.test(value) || /^b'[01]*'$/i.test(value)) return value;
  if (/^\([\s\S]*\)$/.test(value)) return value;
  // A function-call SHAPE only reads as an expression where the encoding is
  // unknown. On a `'raw'` server the value is the stored TEXT, so `uuid()` in a
  // VARCHAR is the six characters, not a call — and 8.0.13+ marks a real
  // expression with `DEFAULT_GENERATED`, handled above.
  if (col.defaultEncoding !== 'raw' && /^[A-Za-z_][A-Za-z0-9_]*\s*\([\s\S]*\)$/.test(value)) {
    return `(${value})`;
  }
  return quoteMysqlLiteral(value);
}

/**
 * ` COLLATE <name>` for a character column that pins a collation. Skipped when
 * it matches the table's own default (noise) and when the table default is not
 * known the collation is pinned anyway — restating the collation a column
 * already has is behaviour-neutral in MySQL, dropping it is not.
 */
function collateClause(col: ColumnMetadata, tableDefaultCollation: string | undefined): string {
  if (!col.collation) return '';
  if (!CHARACTER_TYPES.has(baseTypeName(col.dataType))) return '';
  if (tableDefaultCollation && col.collation === tableDefaultCollation) return '';
  return ` COLLATE ${col.collation}`;
}

/**
 * Build one column definition line in MySQL's declaration order:
 * `<name> <type> [COLLATE] [GENERATED ALWAYS AS (…) STORED|VIRTUAL] <nullability>
 *  [AUTO_INCREMENT] [DEFAULT] [ON UPDATE] [INVISIBLE] [COMMENT]`.
 *
 * Every facet omitted here is DELETED by the Table Designer's first edit of the
 * column: the migration is `MODIFY COLUMN`, which restates the whole definition
 * and diffs against this baseline.
 */
function buildColumnDefinition(col: ColumnMetadata, tableDefaultCollation: string | undefined): string {
  let def = `    ${quoteId(col.name)} ${formatColumnDataType(col)}`;

  if (col.computedExpression) {
    // A generated column takes neither DEFAULT nor AUTO_INCREMENT — the
    // expression governs the value. MySQL cannot ALTER STORED into VIRTUAL, so
    // the declared keyword has to be restated; its default is VIRTUAL.
    // MySQL accepts COLLATE between the type and GENERATED ALWAYS, same as for
    // a non-generated column, so it uses the same `collateClause` rule here.
    def += collateClause(col, tableDefaultCollation);
    const persistence = col.isPersisted === true ? 'STORED' : 'VIRTUAL';
    def += ` GENERATED ALWAYS AS (${col.computedExpression}) ${persistence}`;
    def += col.isNullable ? ' NULL' : ' NOT NULL';
  } else {
    def += collateClause(col, tableDefaultCollation);
    def += col.isIdentity ? ' NOT NULL AUTO_INCREMENT' : (col.isNullable ? ' NULL' : ' NOT NULL');
    const defaultValue = col.isIdentity ? null : formatDefaultValue(col);
    if (defaultValue !== null) def += ` DEFAULT ${defaultValue}`;
    // `ON UPDATE CURRENT_TIMESTAMP` follows DEFAULT in MySQL's grammar, and is
    // reported only in EXTRA (see ColumnMetadata.onUpdate). Omitting it here
    // dropped the auto-update on the first MODIFY of the column.
    if (col.onUpdate) def += ` ON UPDATE ${col.onUpdate}`;
  }

  // `INVISIBLE` sits after the DEFAULT / ON UPDATE pair and after a generated
  // column's STORED|VIRTUAL + nullability, immediately before COMMENT — the slot
  // MySQL's own `SHOW CREATE TABLE` uses (8.0.46 prints
  // `` `s` varchar(10) DEFAULT 'x' /*!80023 INVISIBLE */ COMMENT 'c' ``), so one
  // position serves both branches. Reported only in EXTRA (see
  // `ColumnMetadata.isInvisible`); omitting it here made the Designer's first
  // `MODIFY COLUMN` publish a hidden column.
  if (col.isInvisible) def += ' INVISIBLE';
  if (col.comment) def += ` COMMENT ${quoteMysqlLiteral(col.comment)}`;
  return def;
}

/** Generate CREATE TABLE script from metadata */
function generateCreateScript(ctx: TableScriptContext): string {
  const fullName = qualifyName(ctx.schema, ctx.tableName);

  if (!ctx.metadata?.columns?.length) {
    return `CREATE TABLE ${fullName} (\n    -- Column definitions\n) ENGINE=InnoDB;\n`;
  }

  const tableDefaultCollation = ctx.metadata.engineOptions?.defaultCollation;
  const lines: string[] = [`CREATE TABLE ${fullName} (`];
  lines.push(ctx.metadata.columns.map(col => buildColumnDefinition(col, tableDefaultCollation)).join(',\n'));

  // MySQL's key has no name to keep (`DROP PRIMARY KEY`), but its declared
  // column order is the catalog's (see `resolvePrimaryKey`): listed in
  // table-column order, `PRIMARY KEY (b, a)` was re-created as `(a, b)` on the
  // first key edit.
  const primaryKey = resolvePrimaryKey(ctx.metadata, '');
  if (primaryKey) {
    lines.push(`,    PRIMARY KEY (${primaryKey.columns.map(quoteId).join(', ')})`);
  }

  lines.push(') ENGINE=InnoDB;');
  // The table's indexes follow as CREATE INDEX statements — the Designer's
  // Indexes tab reads them from the buffer (see `indexStatements.ts`).
  return appendIndexStatements(lines.join('\n'), ctx.metadata, { quote: quoteId, table: fullName });
}

/** Generate INSERT template */
function generateInsertScript(ctx: TableScriptContext): string {
  const fullName = qualifyName(ctx.schema, ctx.tableName);

  if (!ctx.metadata?.columns?.length) {
    return `INSERT INTO ${fullName} (column1, column2)\nVALUES (value1, value2);`;
  }

  const insertCols = ctx.metadata.columns.filter(c => !c.isIdentity);
  // D5 — every column is AUTO_INCREMENT. `() VALUES ()` is legal MySQL (unlike
  // MSSQL/PG/Oracle), so the emit is unchanged in substance — just explicit.
  if (insertCols.length === 0) {
    return defaultValuesInsert({ engineId: 'mysql', tableName: fullName });
  }
  const names = insertCols.map(c => quoteId(c.name)).join(', ');
  const values = insertCols.map(c => {
    const lower = c.dataType.toLowerCase();
    if (['int', 'integer', 'bigint', 'smallint', 'tinyint', 'mediumint', 'decimal', 'numeric', 'float', 'double'].some(t => lower.includes(t))) return '0';
    if (lower.includes('bool') || lower === 'tinyint(1)') return 'FALSE';
    if (lower.includes('datetime') || lower.includes('timestamp')) return 'NOW()';
    if (lower.includes('date')) return 'CURDATE()';
    if (lower.includes('time')) return 'CURTIME()';
    return "''";
  }).join(', ');

  return `INSERT INTO ${fullName} (${names})\nVALUES (${values});`;
}

/** Generate UPDATE template */
function generateUpdateScript(ctx: TableScriptContext): string {
  const fullName = qualifyName(ctx.schema, ctx.tableName);

  if (!ctx.metadata?.columns?.length) {
    return `UPDATE ${fullName}\nSET column1 = value1\nWHERE <condition>;`;
  }

  const pkCols = ctx.metadata.columns.filter(c => c.isPrimaryKey);
  const updateCols = ctx.metadata.columns.filter(c => !c.isPrimaryKey && !c.isIdentity);
  const setClauses = updateCols.slice(0, 3).map(c => `    ${quoteId(c.name)} = <value>`).join(',\n');
  const where = pkCols.length > 0
    ? pkCols.map(c => `${quoteId(c.name)} = <value>`).join(' AND ')
    : '<condition>';

  return `UPDATE ${fullName}\nSET\n${setClauses}\nWHERE ${where};`;
}

/** Generate DELETE template */
function generateDeleteScript(ctx: TableScriptContext): string {
  const fullName = qualifyName(ctx.schema, ctx.tableName);

  if (!ctx.metadata?.columns?.length) {
    return `DELETE FROM ${fullName}\nWHERE <condition>;`;
  }

  const pkCols = ctx.metadata.columns.filter(c => c.isPrimaryKey);
  const where = pkCols.length > 0
    ? pkCols.map(c => `${quoteId(c.name)} = <value>`).join(' AND ')
    : '<condition>';

  return `DELETE FROM ${fullName}\nWHERE ${where};`;
}

export const mysqlScriptProvider: ScriptProvider = {
  engine: 'mysql',

  quoteIdentifier: quoteId,

  qualifyTableName(schema: string, table: string): string {
    return qualifyName(schema, table);
  },

  generateTableScript(type: TableScriptType, ctx: TableScriptContext): string {
    const fullName = this.qualifyTableName(ctx.schema, ctx.tableName);

    switch (type) {
      case 'create': return generateCreateScript(ctx);
      case 'drop': return `DROP TABLE IF EXISTS ${fullName};`;
      case 'select': return `SELECT *\nFROM ${fullName};`;
      case 'select-top': return `SELECT *\nFROM ${fullName}\nLIMIT ${SELECT_TOP_ROW_LIMIT};`;
      case 'insert': return generateInsertScript(ctx);
      case 'update': return generateUpdateScript(ctx);
      case 'delete': return generateDeleteScript(ctx);
      default: throw new Error(`Unknown table script type: ${type}`);
    }
  },

  generateDatabaseScript(type: DatabaseScriptType, ctx: DatabaseScriptContext): string {
    const quoted = quoteId(ctx.databaseName);
    const { metadata } = ctx;

    switch (type) {
      case 'create': {
        const charset = metadata?.encoding ? ` CHARACTER SET ${metadata.encoding}` : '';
        const collation = metadata?.collation ? ` COLLATE ${metadata.collation}` : '';
        return `CREATE DATABASE IF NOT EXISTS ${quoted}${charset}${collation};`;
      }
      case 'drop':
        return `-- Warning: This will permanently delete the database and all its data\nDROP DATABASE IF EXISTS ${quoted};`;
      case 'use':
        return `USE ${quoted};`;
      default:
        throw new Error(`Unknown database script type: ${type}`);
    }
  },

  generateNewTableTemplate(ctx: NewObjectTemplateContext): string {
    const prefix = schemaPrefix(ctx.schema);
    return `-- Create new table in ${ctx.database}
CREATE TABLE ${prefix}\`\${1:table_name}\` (
    \`\${2:id}\` INT NOT NULL AUTO_INCREMENT,
    \`\${3:column1}\` \${4:VARCHAR(255)},
    \`created_at\` DATETIME DEFAULT CURRENT_TIMESTAMP,
    PRIMARY KEY (\`\${2:id}\`)
) ENGINE=InnoDB;$0`;
  },

  generateNewViewTemplate(ctx: NewObjectTemplateContext): string {
    const prefix = schemaPrefix(ctx.schema);
    return `-- Create new view in ${ctx.database}
CREATE VIEW ${prefix}\`\${1:view_name}\`
AS
SELECT
    \${2:*}
FROM
    ${prefix}\`\${3:source_table}\`
WHERE
    \${4:TRUE};$0`;
  },

  generateNewProcedureTemplate(ctx: NewObjectTemplateContext): string {
    const prefix = schemaPrefix(ctx.schema);
    return `-- Create new stored procedure in ${ctx.database}
CREATE PROCEDURE ${prefix}\`\${1:procedure_name}\`(
    IN \${2:param1} \${3:INT},
    IN \${4:param2} \${5:VARCHAR(255)}
)
BEGIN
    \${6:-- Procedure logic here}
END;$0`;
  },

  generateNewFunctionTemplate(ctx: NewObjectTemplateContext): string {
    const prefix = schemaPrefix(ctx.schema);
    return `-- Create new function in ${ctx.database}
CREATE FUNCTION ${prefix}\`\${1:function_name}\`(
    \${2:param1} \${3:INT}
)
RETURNS \${4:INT}
DETERMINISTIC
BEGIN
    DECLARE result \${4:INT};
    \${5:-- Function logic here}
    RETURN result;
END;$0`;
  },

  generateNewDatabaseTemplate(): string {
    return `-- Create new database
CREATE DATABASE IF NOT EXISTS \`\${1:database_name}\`
    CHARACTER SET \${2:utf8mb4}
    COLLATE \${3:utf8mb4_unicode_ci};$0`;
  },
};
